import Link from 'next/link';

import {
	FOOTER_COLUMNS,
	FOOTER_LEGAL,
	FOOTER_SOCIAL,
} from '../config/marketing-footer-columns';
import { TAGLINE } from '../constants';
import { MarketingLogo } from '@naumu/ui/components/marketing-logo';

/**
 * MarketingFooterLarge - multi-column public footer mirroring the nav taxonomy
 * (Product · Use cases · Resources, LANDING_INSPIRATION G1/G3). Brand block on
 * the left, link columns on the right, then a legal + copyright row. Columns
 * are driven by config/marketing-footer-columns.
 */
export const MarketingFooterLarge = () => (
	<footer className="px-[clamp(16px,5vw,96px)] pb-12 pt-16">
		<div className="mx-auto max-w-[1440px]">
			<div className="flex flex-col gap-12 lg:flex-row lg:justify-between">
				{/* Brand block */}
				<div className="max-w-xs">
					<MarketingLogo />
					<p className="mt-3 text-[13px] leading-[1.5] text-white/40">
					{TAGLINE}
				</p>
					<div className="mt-5 flex items-center gap-5">
						{FOOTER_SOCIAL.map((link) => {
							const Icon = link.icon;
							return (
								<a
									key={link.href}
									href={link.href}
									target="_blank"
									rel="noopener noreferrer"
									aria-label={link.label}
									className="text-white/40 transition-colors hover:text-white"
								>
									{Icon ? <Icon className="h-4 w-4" /> : link.label}
								</a>
							);
						})}
					</div>
				</div>

				{/* Link columns */}
				<nav
					aria-label="Footer"
					className="grid grid-cols-2 gap-x-10 gap-y-10 sm:grid-cols-3 lg:gap-x-16"
				>
					{FOOTER_COLUMNS.map((column) => (
						<div key={column.title}>
							<h3 className="mb-4 text-[13px] font-semibold text-white">{column.title}</h3>
							<ul className="space-y-3">
								{column.links.map((link) => (
									<li key={link.href}>
										<Link
											href={link.href}
											className="text-[14px] text-white/50 transition-colors hover:text-white"
										>
											{link.label}
										</Link>
									</li>
								))}
							</ul>
						</div>
					))}
				</nav>
			</div>

			{/* Legal row */}
			<div className="mt-16 flex flex-col-reverse items-center gap-4 border-t border-white/[0.06] pt-8 sm:flex-row sm:justify-between">
				<p className="text-[12px] text-white/40">© {new Date().getFullYear()} Naumu</p>
				<nav
					aria-label="Legal"
					className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2"
				>
					{FOOTER_LEGAL.map((link) =>
						link.external ? (
							<a
								key={link.href}
								href={link.href}
								target="_blank"
								rel="noopener noreferrer"
								className="text-[12px] text-white/40 transition-colors hover:text-white"
							>
								{link.label}
							</a>
						) : (
							<Link
								key={link.href}
								href={link.href}
								className="text-[12px] text-white/40 transition-colors hover:text-white"
							>
								{link.label}
							</Link>
						),
					)}
				</nav>
			</div>
		</div>
	</footer>
);
